import { useLocation } from "@reach/router";
import React, { useCallback, useEffect } from "react";
import SEO from "../components/head";
import Layout from "../components/layout";
import Main from "../components/main";
import { EventProvider } from '../context/EventProvider';
import { StrapiSourceData } from "../interfaces/strapi";
import { trackPageview } from "../services";
import { registerV2Alpha } from '../services/auth';
import { useRegistrationStore } from "../store/registrationStore";
import useModalStore from "../store/useModalStore";
import useSessionStore from "../store/useSessionsStore";
import { mergeWithInitialQuery } from "../utils";

const Homepage = ({ pageContext }: { pageContext: StrapiSourceData }) => {
  const { selectedSessions, resetSessions } = useSessionStore();
  const location = useLocation();
  const { updateUserData, userData, resetState } = useRegistrationStore(state => ({
    updateUserData: state.updateUserData,
    userData: state.userData,
    resetState: state.resetState,
  }));
  const { setModalType, openModal } = useModalStore();

  const handleSessionStatus = useCallback(async (session_id: string) => {
    try {
      const response = await fetch(`${process.env.GATSBY_STRIPE_CHECKOUT_SESSION_URI}/session-status?session_id=${session_id}`);
      const session = await response.json();
      if (session.status === 'complete') {
        if (!userData.paymentStep?.completed) {
          updateUserData('paymentStep', { ...userData.paymentStep, completed: true });

          const attendeeDetailsStep = userData.attendeeDetailsStep;
          const firstName = attendeeDetailsStep?.firstName ?? '';
          const lastName = attendeeDetailsStep?.lastName ?? '';
          const email = attendeeDetailsStep?.email ?? '';

          if (firstName && lastName && email) {
            const payload = {
              email: email,
              given_name: firstName,
              family_name: lastName,
              agenda: selectedSessions,
              userData
            };

            try {
              await registerV2Alpha(payload, {});
              resetState();
              resetSessions();
              setModalType('confirm');
              openModal();
            } catch (error) {
              console.error('Registration error:', error);
            }
          } else {
            console.error('Attendee details are incomplete');
          }
        }
      } else {
        if (userData.paymentStep?.completed) {
          updateUserData('paymentStep', { ...userData.paymentStep, completed: false });
        }
        console.log('Something went wrong with payment: ', session.status);
      }
    } catch (error) {
      console.error('Error fetching session status:', error);
    }
  }, [updateUserData, userData, resetState]);

  useEffect(() => {
    if (!userData) return;

    const query = mergeWithInitialQuery(location.search);
    trackPageview(`${location.pathname}?${query}`);

    if (userData.paymentStep?.initiated) {
      const session_id = new URLSearchParams(location.search).get('session_id');
      if (session_id) {
        handleSessionStatus(session_id);
      }
    }
  }, [location, handleSessionStatus, userData]);

  return (
    <EventProvider data={pageContext} >
      <Layout className="page__home">
        <Main />
      </Layout>
    </EventProvider>
  );
};

export default Homepage;

export const Head = ({ pageContext }: { pageContext: StrapiSourceData }) => {
  return (<EventProvider data={pageContext} ><SEO /></EventProvider>);
};
